/**
 * Blob Source (`blob.source`) inspector.
 *
 * Two modes:
 *
 * - **Documents** (the default): a SQL query over the container's listing,
 *   exposed as the table `blobs`. The node outputs one row per matched file,
 *   each with a `_file` reference a downstream node can fetch it by.
 * - **Data file**: the original behaviour — read one tabular file into rows.
 *   A node saved before the document list existed (a `path`, no query, no
 *   `mode`) opens in this mode and keeps doing what it did.
 *
 * Test runs through the same server code a run does and shows the result — or
 * the error — inline. Failures used to surface only as a transient toast (and
 * not at all from the automatic preview), which left the panel empty.
 *
 * The preview is rendered by a component the canvas passes in (`renderPreview`)
 * rather than imported from `PipelineCanvas.tsx`, which imports this file.
 */

import { useCallback, useEffect, useRef, useState, type ReactNode } from "react";
import {
  Alert,
  Button,
  Code,
  SegmentedControl,
  Select,
  Stack,
  Text,
  TextInput,
  Textarea,
} from "@mantine/core";
import { useAccessToken } from "@/features/auth/useAccessToken";
import { listConnections } from "@/shared/api/integration";
import {
  blobListContainers,
  blobListFiles,
  blobQueryDocuments,
  blobReadTable,
  type BlobDocumentsResult,
  type ReadTableResult,
} from "@/shared/api/integration-browse";
import { ApiError } from "@/shared/api/apiFetch";
import { toastApiError } from "@/shared/ui/toast";

import { ConnectionRefSelect } from "./ConnectionRefSelect";
import { useConnectionRef } from "./useConnectionRef";
import { BLOBS_COLUMNS, DEFAULT_BLOB_QUERY, blobSourceMode } from "./blobSource";

/** How many matched rows the preview shows. */
const PREVIEW_ROWS = 50;

const DELIMITER_OPTIONS = [
  { value: "", label: "Auto-detect" },
  { value: ",", label: "Comma  ," },
  { value: ";", label: "Semicolon  ;" },
  { value: "\t", label: "Tab" },
  { value: "|", label: "Pipe  |" },
];

/** Union of column names across rows, first-seen order. */
function columnsOf(rows: Record<string, unknown>[]): string[] {
  const seen = new Set<string>();
  const cols: string[] = [];
  for (const r of rows) {
    for (const k of Object.keys(r)) {
      if (!seen.has(k)) {
        seen.add(k);
        cols.push(k);
      }
    }
  }
  return cols;
}

/** The message to show inline for a failed preview. */
function errorText(err: unknown): string {
  if (err instanceof ApiError || err instanceof Error) return err.message;
  return String(err);
}

type Props = {
  config: Record<string, unknown>;
  editable: boolean;
  onChange: (key: string, value: unknown) => void;
  onColumns?: (cols: string[]) => void;
  /** How to render the previewed rows — the canvas passes its `DataView`. */
  renderPreview: (rows: Record<string, unknown>[]) => ReactNode;
};

export function BlobSourceConfig(props: Props) {
  const { config, editable, onChange } = props;
  const getToken = useAccessToken();
  const { connectionId, role, settled } = useConnectionRef(String(config.connectionId ?? ""));
  const container = String(config.container ?? "");
  const mode = blobSourceMode(config);

  const [connections, setConnections] = useState<{ value: string; label: string }[]>([]);
  const [containers, setContainers] = useState<string[]>([]);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const items = (await listConnections(await getToken(), "blob")).items;
        if (!cancelled)
          setConnections(items.map((c) => ({ value: c.connection_id, label: c.display_name })));
      } catch (err) {
        toastApiError("Could not load connections", err);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [getToken]);

  useEffect(() => {
    if (!connectionId) {
      setContainers([]);
      return;
    }
    let cancelled = false;
    void (async () => {
      try {
        const items = (await blobListContainers(await getToken(), connectionId)).items;
        if (!cancelled) setContainers(items.map((c) => c.name));
      } catch (err) {
        toastApiError("Could not load containers", err);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [getToken, connectionId]);

  return (
    <Stack gap="xs">
      <ConnectionRefSelect
        label="Blob connection"
        placeholder={connections.length ? "Pick a connection" : "No Blob connections set up"}
        data={connections}
        nothingFoundMessage="No Blob connections"
        connectionId={connectionId}
        role={role}
        settled={settled}
        editable={editable}
        onChange={(v) => {
          const name = connections.find((c) => c.value === v)?.label ?? "";
          onChange("connectionId", v);
          onChange("connectionName", name);
          onChange("container", "");
          onChange("path", "");
        }}
      />

      {connectionId && (
        <Select
          label="Container"
          placeholder="Pick a container"
          data={containers}
          value={container || null}
          disabled={!editable}
          searchable
          onChange={(v) => {
            onChange("container", v ?? "");
            onChange("path", "");
          }}
        />
      )}

      <SegmentedControl
        size="xs"
        fullWidth
        value={mode}
        disabled={!editable}
        aria-label="What to read"
        onChange={(v) => {
          onChange("mode", v);
          if (v === "documents" && !String(config.query ?? "").trim())
            onChange("query", DEFAULT_BLOB_QUERY);
        }}
        data={[
          { value: "documents", label: "Documents (SQL)" },
          { value: "file", label: "A data file" },
        ]}
      />

      {mode === "documents" ? (
        <DocumentsMode {...props} connectionId={connectionId} container={container} />
      ) : (
        <FileMode {...props} connectionId={connectionId} container={container} />
      )}
    </Stack>
  );
}

type ModeProps = Props & { connectionId: string; container: string };

/** Documents mode: prefix + SQL over `blobs`, Test shows the matched files. */
function DocumentsMode({
  config,
  editable,
  onChange,
  onColumns,
  renderPreview,
  connectionId,
  container,
}: ModeProps) {
  const getToken = useAccessToken();
  const prefix = String(config.prefix ?? "");
  const query = String(config.query ?? "");
  const [testing, setTesting] = useState(false);
  const [preview, setPreview] = useState<BlobDocumentsResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const ready = Boolean(connectionId && container);

  const previewKey = ["documents", connectionId, container, prefix, query].join("|");
  const savedKey = String(config.previewKey ?? "");
  const attemptedKey = useRef("");

  const loadPreview = useCallback(async () => {
    if (!connectionId || !container) return;
    setTesting(true);
    setError(null);
    try {
      const result = await blobQueryDocuments(await getToken(), connectionId, container, query, {
        prefix,
        limit: PREVIEW_ROWS,
      });
      setPreview(result);
      const cols = result.rows.length ? columnsOf(result.rows) : [...BLOBS_COLUMNS, "_file"];
      onColumns?.(cols);
      onChange("previewColumns", cols);
      onChange("previewKey", previewKey);
    } catch (err) {
      setPreview(null);
      setError(errorText(err));
    } finally {
      setTesting(false);
    }
  }, [getToken, connectionId, container, query, prefix, onChange, onColumns, previewKey]);

  // Auto-load once, when the node is first fully configured and its saved
  // columns are stale, so downstream column pickers fill without a manual
  // Test. Once only: re-listing a container on every keystroke of the query
  // would be a listing per character. Its error is shown inline.
  useEffect(() => {
    if (!ready || attemptedKey.current) return;
    attemptedKey.current = previewKey;
    if (savedKey === previewKey) return;
    void loadPreview();
  }, [ready, savedKey, previewKey, loadPreview]);

  const columns = preview?.columns ?? [...BLOBS_COLUMNS];

  return (
    <>
      <TextInput
        label="Folder prefix (optional)"
        description="Only list files under this path, e.g. etmf/site-001/. Empty lists the whole container."
        placeholder="etmf/"
        value={prefix}
        disabled={!editable}
        onChange={(e) => onChange("prefix", e.currentTarget.value)}
      />
      <Textarea
        label="SQL — which documents"
        description="One SELECT over the table blobs: one row per file in the container."
        placeholder={DEFAULT_BLOB_QUERY}
        autosize
        minRows={3}
        styles={{ input: { fontFamily: "var(--mantine-font-family-monospace)" } }}
        value={query}
        disabled={!editable}
        onChange={(e) => onChange("query", e.currentTarget.value)}
      />
      <Text size="xs" c="dimmed" data-testid="blobs-columns">
        Columns of <Code>blobs</Code>: {columns.join(", ")}
        {preview ? "" : ", plus meta_<key> for each metadata key"}. Dates are UTC text (
        <Code>last_modified &gt;= &apos;2026-01-01&apos;</Code>). Keep <Code>path</Code> in the
        SELECT: each row&apos;s <Code>_file</Code> reference is found by it.
      </Text>

      <Button
        variant="light"
        onClick={() => void loadPreview()}
        loading={testing}
        disabled={!ready}
      >
        Test — preview data
      </Button>

      {error && (
        <Alert color="red" variant="light" p="xs" title="Query failed">
          <Text size="xs" style={{ whiteSpace: "pre-wrap" }}>
            {error}
          </Text>
        </Alert>
      )}

      {preview && (
        <Stack gap={4}>
          <Text size="xs" c="dimmed">
            {preview.row_count.toLocaleString()} of {preview.listed.toLocaleString()}{" "}
            {preview.listed === 1 ? "file" : "files"} matched
            {preview.row_count > preview.rows.length
              ? ` · showing the first ${preview.rows.length}`
              : ""}
          </Text>
          {preview.listing_truncated && (
            <Text size="xs" c="yellow.7">
              The listing stopped at {preview.listed.toLocaleString()} files — set a folder prefix
              to narrow it.
            </Text>
          )}
          {preview.truncated && (
            <Text size="xs" c="yellow.7">
              More matches than a run keeps — it stops at {preview.row_count.toLocaleString()} rows.
            </Text>
          )}
          {preview.row_count > 0 && preview.linked < preview.row_count && (
            <Text size="xs" c="yellow.7">
              {preview.row_count - preview.linked} rows name no file (no <Code>path</Code> column),
              so their <Code>_file</Code> is empty.
            </Text>
          )}
          {preview.rows.length > 0 ? (
            renderPreview(preview.rows)
          ) : (
            <Text size="xs" c="dimmed">
              No files match.
            </Text>
          )}
        </Stack>
      )}
    </>
  );
}

/** Data-file mode: the original single-file reader. */
function FileMode({
  config,
  editable,
  onChange,
  onColumns,
  renderPreview,
  connectionId,
  container,
}: ModeProps) {
  const getToken = useAccessToken();
  const path = String(config.path ?? "");
  const sheet = String(config.sheet ?? "");
  const delimiter = String(config.delimiter ?? "");
  const isExcel = /\.xlsx$/i.test(path);
  const isDelimited = /\.(csv|tsv)$/i.test(path);

  const [files, setFiles] = useState<string[]>([]);
  const [testing, setTesting] = useState(false);
  const [preview, setPreview] = useState<ReadTableResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!connectionId || !container) {
      setFiles([]);
      return;
    }
    let cancelled = false;
    void (async () => {
      try {
        const items = (await blobListFiles(await getToken(), connectionId, container, null)).items;
        if (!cancelled) setFiles(items.filter((f) => !f.name.endsWith("/")).map((f) => f.name));
      } catch (err) {
        toastApiError("Could not list files", err);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [getToken, connectionId, container]);

  const previewKey = [
    connectionId,
    container,
    path,
    isExcel ? sheet : "",
    isDelimited ? delimiter : "",
  ].join("|");
  const savedKey = String(config.previewKey ?? "");
  const attemptedKey = useRef("");

  const loadPreview = useCallback(async () => {
    if (!connectionId || !container || !path) return;
    setTesting(true);
    setError(null);
    try {
      const result = await blobReadTable(await getToken(), connectionId, container, path, {
        sheet: isExcel ? sheet : null,
        delimiter: isDelimited ? delimiter : null,
      });
      const cols = columnsOf(result.rows);
      setPreview(result);
      onColumns?.(cols);
      onChange("previewColumns", cols);
      onChange("previewKey", previewKey);
    } catch (err) {
      setPreview(null);
      setError(errorText(err));
    } finally {
      setTesting(false);
    }
  }, [
    getToken,
    connectionId,
    container,
    path,
    isExcel,
    sheet,
    isDelimited,
    delimiter,
    onChange,
    onColumns,
    previewKey,
  ]);

  useEffect(() => {
    if (!connectionId || !container || !path) return;
    if (savedKey === previewKey || attemptedKey.current === previewKey) return;
    attemptedKey.current = previewKey;
    void loadPreview();
  }, [connectionId, container, path, previewKey, savedKey, loadPreview]);

  return (
    <>
      {connectionId && container && (
        <Select
          label="File"
          placeholder={files.length ? "Pick a file" : "No files in this container"}
          description="CSV, TSV, JSON, JSONL, or Excel (.xlsx). For PDFs and other documents, use Documents (SQL)."
          data={files}
          value={path || null}
          disabled={!editable}
          searchable
          nothingFoundMessage="No files"
          onChange={(v) => {
            onChange("path", v ?? "");
            setPreview(null);
          }}
        />
      )}

      {isExcel && (
        <TextInput
          label="Sheet (optional)"
          placeholder="First sheet"
          value={sheet}
          disabled={!editable}
          onChange={(e) => onChange("sheet", e.currentTarget.value)}
        />
      )}

      {isDelimited && (
        <Select
          label="Delimiter"
          description="How columns are separated. Auto-detect handles most files."
          data={DELIMITER_OPTIONS}
          value={delimiter}
          disabled={!editable}
          allowDeselect={false}
          onChange={(v) => {
            onChange("delimiter", v ?? "");
            setPreview(null);
          }}
        />
      )}

      <Button
        variant="light"
        onClick={() => void loadPreview()}
        loading={testing}
        disabled={!connectionId || !container || !path}
      >
        Test — preview data
      </Button>

      {error && (
        <Alert color="red" variant="light" p="xs" title="Preview failed">
          <Text size="xs">{error}</Text>
        </Alert>
      )}

      {preview && (
        <Stack gap={4}>
          {preview.truncated && (
            <Text size="xs" c="yellow.7">
              Large file — showing the first {preview.row_count} rows.
            </Text>
          )}
          {isDelimited && !delimiter && preview.delimiter && (
            <Text size="xs" c="dimmed">
              Detected delimiter: {preview.delimiter === "\t" ? "Tab" : `"${preview.delimiter}"`}
            </Text>
          )}
          {renderPreview(preview.rows)}
        </Stack>
      )}
    </>
  );
}
